import {
  ApolloClient,
  ApolloLink,
  ApolloProvider,
  createHttpLink,
  InMemoryCache,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/link-context';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import React, { useMemo, useEffect } from 'react';
import getConfig from 'next/config';

const { publicRuntimeConfig } = getConfig();

import { useSession } from 'next-auth/react';

const apolloClient =
new ApolloClient({
  link: undefined,
  cache: new InMemoryCache(),
  connectToDevTools: true,
});

const AuthorizedApolloProvider = ({ children, graphqlApi }: any) => {
  let { data: session }: any = useSession();
  const token = session?.accessToken;
  const httpLink = createHttpLink({
    uri: graphqlApi,
  });


  // Helper function to transform GraphQL URI to WebSocket URI
  const getWebSocketUri = (uri: string): string => {
    if (uri?.startsWith('https://')) {
      return uri.replace('https://', 'wss://');
    } else if (uri?.startsWith('http://')) {
      return uri.replace('http://', 'ws://');
    }
    return uri;
  };

  // Create WebSocket link for subscriptions with optimized configuration
  const wsLink = useMemo(() => {
    if (!graphqlApi) return null;
    
    const wsUri = getWebSocketUri(graphqlApi);
    
    return new GraphQLWsLink(createClient({
      url: wsUri,
      connectionParams: () => ({
        Authorization: token ? `Bearer ${token}` : '',
      }),
      retryAttempts: 5,
      keepAlive: 30000, // Keep connection alive for 30 seconds
      lazy: true, // Only connect when subscription is active
      connectionAckWaitTimeout: 30000, // Wait up to 30 seconds for connection ack
    }));
  }, [graphqlApi, token]);

  const authLink = useMemo(() => {
    return setContext(async () => {
      if (token) {
        return {
          headers: {
            Authorization: `Bearer ${token}`
          }
        };
      }
    });
  }, [token]);

  // Split link to route queries/mutations to HTTP and subscriptions to WebSocket
  const splitLink = useMemo(() => {
    if (wsLink) {
      return split(
        ({ query }) => {
          const definition = getMainDefinition(query);
          return (
            definition.kind === 'OperationDefinition' &&
            definition.operation === 'subscription'
          );
        },
        wsLink,
        authLink.concat(httpLink)
      );
    }
    return authLink.concat(httpLink);
  }, [wsLink, authLink, httpLink]);

  // Optimize link setting to prevent unnecessary updates
  useMemo(() => {
    apolloClient.setLink(splitLink);
  }, [splitLink]);

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};

export default AuthorizedApolloProvider;
